import { Route, Navigate, createRoutesFromElements } from 'react-router-dom'
// App is still used for ThemeProvider, CssBaseline, but MainLayout handles the visual structure
import App from './components/App'
import MainLayout from './components/MainLayout' // Use the standard MainLayout
import AuthenticatedRoute from './components/AuthenticatedRoute'
import FundingRequestsPage from './pages/FundingRequestsPage'
import NotFound from './pages/NotFound'
import ProfilePage from './pages/ProfilePage'
import MatchPage from './pages/MatchPage'
import MatchResults from './pages/MatchResults'
import ApplicationsListPage from './pages/ApplicationsListPage'
import ApplicationPage from './pages/ApplicationPage'
import DatabaseSearchPage from './pages/DatabaseSearchPage'
import AnnualOnlyRoute from './components/AnnualOnlyRoute'
import ApplicationsNeedListPage from './pages/ApplicationsNeedListPage'
import ErrorMessage from './components/ErrorMessage'
import LandingPage from './pages/LandingPage'
import FeedbackPage from './pages/FeedbackPage'
import AccountPage from './pages/AccountPage'
import HelpFormPage from './pages/HelpFormPage'
import OrgUsersPage from './pages/OrgUsersPage'
import InvitationPage from './pages/InvitationPage'
import { RouteGuard } from './utils/routeGuard/RouteGuard'
import OnboardingShell from './pages/onboarding/OnboardingShell'
import OnboardingNamePage from './pages/onboarding/OnboardingNamePage'
import SignupPage from './pages/SignupPage'
import SurveyPage from './pages/SurveyPage'

export const routes = createRoutesFromElements(
  // App provides Theme and CssBaseline globally
  <Route element={<App />}>
    {/* Onboarding V2 — full-page layout, no MainLayout */}
    <Route element={<AuthenticatedRoute />}>
      <Route
        path="/onboarding/name"
        element={<OnboardingNamePage />}
        errorElement={<ErrorMessage />}
      />
      <Route
        path="/onboarding"
        element={<OnboardingShell />}
        errorElement={<ErrorMessage />}
      />
    </Route>

    {/* MainLayout provides Nav, Onboarding Banner, and content area */}
    <Route element={<MainLayout />}>
      {/* AuthenticatedRoute wraps all routes that need authentication */}
      <Route element={<AuthenticatedRoute />}>
        <Route
          index
          element={<LandingPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/add-org"
          element={<Navigate to="/onboarding?new=true" replace />}
        />

        {/* Main App Routes */}
        <Route
          path="/funding-requests"
          element={<FundingRequestsPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/match"
          element={<MatchPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/match/:fundingNeedId"
          element={<MatchResults />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/applications"
          element={<ApplicationsNeedListPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/applications/:fundingNeedId"
          element={<ApplicationsListPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/applications/:fundingNeedId/:applicationId"
          element={<ApplicationPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/search"
          element={
            <AnnualOnlyRoute>
              <DatabaseSearchPage />
            </AnnualOnlyRoute>
          }
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/profile"
          element={<ProfilePage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/profile/:tab"
          element={<ProfilePage />}
          errorElement={<ErrorMessage />}
        />

        <Route
          path="/my-account"
          element={<ProfilePage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/notifications"
          element={<ProfilePage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/account"
          element={
            <RouteGuard allowedPermissions={['admin']}>
              <AccountPage />
            </RouteGuard>
          }
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/account/success"
          element={
            <RouteGuard allowedPermissions={['admin']}>
              <AccountPage />
            </RouteGuard>
          }
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/help"
          element={<HelpFormPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/feedback"
          element={<FeedbackPage />}
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/users"
          element={
            <RouteGuard allowedPermissions={['admin']}>
              <OrgUsersPage />{' '}
            </RouteGuard>
          }
          errorElement={<ErrorMessage />}
        />
        <Route
          path="/invite/:invitationToken"
          element={<InvitationPage />}
          errorElement={<ErrorMessage />}
        />
      </Route>
      {/* Routes outside AuthenticatedRoute but within MainLayout (e.g., public pages with nav) */}
      <Route path="*" element={<NotFound />} errorElement={<ErrorMessage />} />
    </Route>
    {/* Other routes outside MainLayout — plain pages without nav/banner */}
    <Route
      path="/signup"
      element={<SignupPage />}
      errorElement={<ErrorMessage />}
    />
    {/* Public, no-login survey reached only via a unique link in a 3F/4F email */}
    <Route
      path="/survey/:uuid"
      element={<SurveyPage />}
      errorElement={<ErrorMessage />}
    />
  </Route>,
)
